import mixpanel from 'mixpanel-browser';

// ============================================
// MIXPANEL ANALYTICS UTILITY
// ============================================

// Page view tracking
export const trackPageView = (pageName: string, properties?: Record<string, any>) => {
  mixpanel.track('Page View', {
    page: pageName,
    ...properties,
  });
};

// Navigation tracking
export const trackNavigation = (from: string, to: string, method?: string) => {
  mixpanel.track('Navigation', {
    from_page: from,
    to_page: to,
    navigation_method: method || 'button', // 'button', 'bottom_nav', 'back', 'link'
  });
};

// Button click tracking
export const trackClick = (
  buttonName: string,
  pageName: string,
  properties?: Record<string, any>,
) => {
  mixpanel.track('Click', {
    button: buttonName,
    page: pageName,
    ...properties,
  });
};

// Selection tracking
export const trackSelection = (
  selectionType: string,
  selectedValue: any,
  properties?: Record<string, any>,
) => {
  mixpanel.track('Selection', {
    selection_type: selectionType,
    selected_value: selectedValue,
    ...properties,
  });
};

// Onboarding flow tracking
export const trackOnboardingStep = (step: string, data?: Record<string, any>) => {
  mixpanel.track('Onboarding Step', {
    step,
    ...data,
  });
};

// Practice-related events
export const trackPracticeAction = (
  action: string,
  verseIndex?: number,
  properties?: Record<string, any>,
) => {
  mixpanel.track('Practice Action', {
    action, // 'play', 'pause', 'record_start', 'record_stop', 'compare', 'share'
    verse_index: verseIndex,
    ...properties,
  });
};

// Learning curve selection
export const trackLearningCurveChange = (option: number | string, pageName: string) => {
  mixpanel.track('Learning Curve Change', {
    option: String(option),
    page: pageName,
  });
};

// Sign in / authentication events
export const trackAuth = (action: string, method?: string, success?: boolean) => {
  mixpanel.track('Auth', {
    action, // 'sign_in', 'sign_out', 'sign_up'
    method, // 'google', 'facebook', 'guest'
    success,
  });
};

// Rabbi selection
export const trackRabbiSelection = (rabbiId: string, rabbiName: string, tradition: string) => {
  mixpanel.track('Rabbi Selected', {
    rabbi_id: rabbiId,
    rabbi_name: rabbiName,
    tradition,
  });
};

// Tradition selection
export const trackTraditionSelection = (tradition: string, length: string) => {
  mixpanel.track('Tradition Selected', {
    tradition,
    length,
  });
};

// Parasha selection
export const trackParashaSelection = (
  parashaEnglish: string,
  parashaHebrew: string,
  birthDate?: string,
) => {
  mixpanel.track('Parasha Selected', {
    parasha_english: parashaEnglish,
    parasha_hebrew: parashaHebrew,
    birth_date: birthDate,
  });
};

// Comparison result tracking
export const trackComparisonResult = (score: number, verseIndex: number, parasha?: string) => {
  mixpanel.track('Comparison Result', {
    score,
    verse_index: verseIndex,
    parasha,
  });
};

// Language switch tracking
export const trackLanguageSwitch = (fromLang: string, toLang: string) => {
  mixpanel.track('Language Switch', {
    from_language: fromLang,
    to_language: toLang,
  });
};

// Audio playback tracking
export const trackAudioPlayback = (
  action: 'play' | 'pause' | 'stop',
  audioType: 'rabbi' | 'user',
  verseIndex?: number,
) => {
  mixpanel.track('Audio Playback', {
    action,
    audio_type: audioType,
    verse_index: verseIndex,
  });
};

// Rabbi audio upload / management events
export const trackRabbiAudioEvent = (action: string, properties?: Record<string, any>) => {
  mixpanel.track('Rabbi Audio', {
    action,
    ...properties,
  });
};

// Purchase / subscription events
export const trackPurchase = (
  productId: string,
  method: 'store' | 'coupon',
  properties?: Record<string, any>,
) => {
  mixpanel.track('Purchase Completed', {
    product_id: productId,
    method, // 'store' (native IAP), 'coupon' (free coupon redemption)
    ...properties,
  });
};

// Set user properties (call after login)
export const setUserProperties = (properties: Record<string, any>) => {
  mixpanel.people.set(properties);
};

// Identify user (call after login)
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  mixpanel.identify(userId);
  if (properties) {
    mixpanel.people.set(properties);
  }
};

// Reset user (call on logout)
export const resetUser = () => {
  mixpanel.reset();
};
